import type { ColumnGroupType, ColumnsType } from 'antd/es/table';

import IncrementBadge from '@/components/Badge/IncrementBadge';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import {
  CHART_ROW_TOTAL_COLUMN_KEY,
  CHART_TOOLTIP_TARGET_TYPE,
  CHART_TOTAL_ROW_KEY,
} from '@/entities/chart/model/constants';
import type { CrossTableColumn } from '@/features/(charts)/chart-renderer/ui/Cross/types';

import { ColumnBuilder } from './ColumnBuilder';

/**
 * 비교 모드 컬럼 빌더
 * Previous / Current / %Change 서브 컬럼 구조를 생성
 */
export class CompareColumnBuilder extends ColumnBuilder {
  /**
   * 비교 모드 컬럼 생성
   * - baseColumns + yHeaders (각각 Previous/Current/%Change 서브 컬럼 포함)
   * - showRowTotal이 true인 경우 Row Total 컬럼 추가
   */
  buildColumns(): ColumnsType<CrossTableColumn> {
    const { chart, baseColumns, yHeaders, helpers, configs } = this.params;
    const primaryConfig = configs[0];

    if (!primaryConfig || !chart.series[0]?.cross_field) {
      throw new Error('cross_field is required');
    }

    const crossFieldType = chart.series[0].cross_field.type;

    const columns: ColumnsType<CrossTableColumn> = [...baseColumns];

    const compareColumns = yHeaders.map((alias) => {
      return {
        title: (
          <Tooltip tooltipNode={helpers.getLabel(crossFieldType, alias)}>
            <Typo>{helpers.getLabel(crossFieldType, alias)}</Typo>
          </Tooltip>
        ),
        dataIndex: alias,
        key: alias,
        children: [
          {
            title: `Previous`,
            width: 100,
            dataIndex: `${alias}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`,
            key: `${alias}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`,
            render: (
              value: number,
              row: Record<string, StringNumberValue | null>,
              index: number,
            ) =>
              this.createCellRenderer(
                value,
                index,
                alias,
                true,
                row.key === CHART_TOTAL_ROW_KEY,
                0,
              ),
          },
          {
            title: 'Current',
            width: 100,
            dataIndex: `${alias}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`,
            key: `${alias}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`,
            sortOrder: 'descend',
            render: (
              value: number,
              row: Record<string, StringNumberValue | null>,
              index: number,
            ) =>
              this.createCellRenderer(
                value,
                index,
                alias,
                false,
                row.key === CHART_TOTAL_ROW_KEY,
              ),
          },
          {
            title: '%Change',
            width: 100,
            dataIndex: `${alias}-change`,
            key: `${alias}-change`,
            render: (
              text: number,
              row: Record<string, StringNumberValue | null>,
            ) => {
              return <IncrementBadge amount={text} />;
            },
          },
        ],
      };
    }) as unknown as ColumnsType<CrossTableColumn>;

    columns.push(...compareColumns);

    // Add Row Total column only if showRowTotal is true
    if (primaryConfig.showRowTotal) {
      const rowTotalColumn = {
        title: (
          <Tooltip tooltipNode="Row Total">
            <Typo className="text-gray-700">Total</Typo>
          </Tooltip>
        ),
        dataIndex: CHART_ROW_TOTAL_COLUMN_KEY,
        key: CHART_ROW_TOTAL_COLUMN_KEY,
        children: [
          {
            title: `Previous`,
            width: 100,
            dataIndex: `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`,
            key: `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`,
            render: (
              value: number,
              row: Record<string, StringNumberValue | null>,
              index: number,
            ) =>
              this.createCellRenderer(
                value,
                index,
                CHART_ROW_TOTAL_COLUMN_KEY,
                true,
                true,
              ),
          },
          {
            title: 'Current',
            width: 100,
            dataIndex: `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`,
            key: `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`,
            render: (
              value: number,
              row: Record<string, StringNumberValue | null>,
              index: number,
            ) =>
              this.createCellRenderer(
                value,
                index,
                CHART_ROW_TOTAL_COLUMN_KEY,
                false,
                true,
              ),
          },
          {
            title: '%Change',
            width: 100,
            dataIndex: `${CHART_ROW_TOTAL_COLUMN_KEY}-change`,
            key: `${CHART_ROW_TOTAL_COLUMN_KEY}-change`,
            render: (
              text: number,
              row: Record<string, StringNumberValue | null>,
            ) => {
              return <IncrementBadge amount={text} />;
            },
          },
        ],
      };

      columns.push(
        rowTotalColumn as unknown as ColumnGroupType<CrossTableColumn>,
      );
    }

    return columns;
  }
}
